/**
 * Detect panel accumulation without serializing the spec or scanning panels.
 * Reads only section flags and array lengths, so cost scales with sections,
 * not panel or override counts.
 */
import {type ViewType} from './types';

type LoosePanelBankConfig = {
  sections?: Array<{
    panels?: unknown[];
    isPanelsAuto?: boolean;
  }>;
};

type SpecCounts = {
  sectionCount: number;
  storedPanelCount: number;
  /**
   * Populated sections with `isPanelsAuto !== true`; shows how widespread
   * this state is, not whether compression will fail.
   */
  populatedSectionsNotMarkedAuto: number;
  /**
   * Panels held by those sections; reveals concentration hidden by section
   * counts. Describes stored structure, not whether compression succeeded.
   */
  panelsInSectionsNotMarkedAuto: number;
  /** Populated sections with no auto flag; distinguishes missing from false. */
  unflaggedSectionCount: number;
};

/** Multi-run workspaces nest the panel bank under section; run workspaces do not. */
function getPanelBankConfig(
  spec: unknown,
  viewType: ViewType
): LoosePanelBankConfig | undefined {
  if (spec == null || typeof spec !== 'object') {
    return undefined;
  }
  const s = spec as {
    section?: {panelBankConfig?: LoosePanelBankConfig};
    panelBankConfig?: LoosePanelBankConfig;
  };
  switch (viewType) {
    case 'run-view':
      return s.panelBankConfig;
    case 'project-view':
    case 'group-view':
    case 'sweep-view':
      return s.section?.panelBankConfig;
    default:
      return undefined;
  }
}

export function getLoadSpecStats(view: {
  spec?: unknown;
  type: ViewType;
}): SpecCounts {
  const pbc = getPanelBankConfig(view.spec, view.type);
  if (pbc?.sections == null) {
    return {
      sectionCount: 0,
      storedPanelCount: 0,
      populatedSectionsNotMarkedAuto: 0,
      panelsInSectionsNotMarkedAuto: 0,
      unflaggedSectionCount: 0,
    };
  }

  let storedPanelCount = 0;
  let populatedSectionsNotMarkedAuto = 0;
  let panelsInSectionsNotMarkedAuto = 0;
  let unflaggedSectionCount = 0;
  for (const section of pbc.sections) {
    const panelCount = section?.panels?.length ?? 0;
    storedPanelCount += panelCount;
    if (panelCount > 0 && section?.isPanelsAuto !== true) {
      populatedSectionsNotMarkedAuto++;
      panelsInSectionsNotMarkedAuto += panelCount;
      if (section?.isPanelsAuto === undefined) {
        unflaggedSectionCount++;
      }
    }
  }

  return {
    sectionCount: pbc.sections.length,
    storedPanelCount,
    populatedSectionsNotMarkedAuto,
    panelsInSectionsNotMarkedAuto,
    unflaggedSectionCount,
  };
}
